import type { SupabaseClient } from '@supabase/supabase-js'
import { bookable } from '../../../api/src/lib/bookable'
import { getActiveClient } from './active-session'
import { dataClient } from './supabase'
import { solveTurnstile } from './turnstile'

/** Retry a read once on transient failure (cold cache / statement timeout), with a short backoff.
 *  Reads are idempotent, so retrying is always safe; a persistent failure still rejects (and is
 *  logged at the call site) rather than being swallowed into a permanent empty state. */
async function withRetry<T>(fn: () => Promise<T>, tries = 2, delayMs = 500): Promise<T> {
  let lastErr: unknown
  for (let i = 0; i < tries; i++) {
    try {
      return await fn()
    } catch (e) {
      lastErr = e
      if (i < tries - 1) await new Promise((r) => setTimeout(r, delayMs))
    }
  }
  throw lastErr
}

export interface IndexRow {
  line_category_id: number
  line_category: string
  week: string
  lane_count: number
  sample_size: number
  avg_20gp: number | null
  avg_40gp: number | null
  avg_40hc: number | null
  median_40hc: number | null
  min_40hc: number | null
  max_40hc: number | null
  avg_transit_days: number | null
  currency: string
}

export interface LaneRow {
  pol_code: string
  pol_name: string | null
  pol_country: string | null
  pol_terminal: string | null
  pod_code: string
  pod_name: string | null
  pod_country: string | null
  pod_terminal: string | null
  carrier: string | null
  carrier_logo: string | null
  vessel: string | null
  voyage: string | null
  route_code: string | null
  service_mode: string | null
  is_via: boolean | null
  transit_days: number | null
  etd: string | null
  eta: string | null
  price_20gp: number | null
  price_40gp: number | null
  price_40hc: number | null
  currency: string | null
  line_category: string | null
  line_category_id: number | null
  source_updated_at: string | null
}

export const laneSlug = (pol: string, pod: string) => `${pol}-${pod}`.toLowerCase()
export const parseLaneSlug = (slug: string) => {
  // Tolerate a trailing ".html" (direct static-file hits) alongside the clean-URL form.
  const [pol, pod] = slug
    .replace(/\.html$/i, '')
    .toUpperCase()
    .split('-')
  return pol && pod ? { pol, pod } : null
}

/** USD price → "$3,920". */
export const fmtPrice = (n: number | null | undefined) =>
  n == null ? '—' : `$${Math.round(n).toLocaleString('en-US')}`

/** Display port name: strip the polluted trailing ",Xx" country/state suffix and title-case, so
 *  "HAMBURG,DE" → "Hamburg" and "New York,Ny" → "New York". Falls back to the UN/LOCODE. */
export const cleanPort = (name?: string | null, code = ''): string => {
  const raw = (name || code || '').replace(/,\s*[A-Za-z]{2}\.?$/, '').trim()
  return raw.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())
}

export async function getIndexLatest(): Promise<IndexRow[]> {
  return withRetry(async () => {
    const { data, error } = await dataClient.from('freight_rate_index_latest').select('*')
    if (error) throw error
    return (data ?? []) as IndexRow[]
  })
}

export async function getIndexWeekly(categoryId: number): Promise<IndexRow[]> {
  const { data, error } = await dataClient
    .from('freight_rate_index_weekly')
    .select('*')
    .eq('line_category_id', categoryId)
    .order('week', { ascending: true })
  if (error) throw error
  return (data ?? []) as IndexRow[]
}

/** All weekly index rows (every trade bucket) in one query — powers the hero tape sparklines. */
export async function getIndexWeeklyAll(): Promise<IndexRow[]> {
  return withRetry(async () => {
    const { data, error } = await dataClient
      .from('freight_rate_index_weekly')
      .select('*')
      .order('week', { ascending: true })
    if (error) throw error
    return (data ?? []) as IndexRow[]
  })
}

export async function getLanes(): Promise<LaneRow[]> {
  // NOTE: no line_category_id filter — that column is an unrelated FreighTower grouping. Callers
  // that need a trade slice classify by destination client-side via tradeBucket(pod_code).
  // Richest cards first (vessel + full prices), then freshest — so complete FreighTower cards lead
  // and data-sparse ones fall to the back.
  return withRetry(async () => {
    const { data, error } = await dataClient
      .from('freight_rate_lane_latest')
      .select('*')
      .order('data_rank', { ascending: false })
      .order('source_updated_at', { ascending: false })
    if (error) throw error
    return (data ?? []) as LaneRow[]
  })
}

// ── Full per-sailing card (freight_rate_lane_cards) — everything the harvest carries ──

export interface TransshipLeg {
  portName?: string | null
  portPier?: string | null
  vessel?: string | null
  voyage?: string | null
  serviceCode?: string | null
  serviceName?: string | null
  transportMode?: string | null
  etd?: number | null
  eta?: number | null
  siCutOff?: number | null
  cyCutOff?: number | null
  closeCutOff?: number | null
}

export interface CtnPrice {
  currency?: string | null
  basePrice?: number | null
  totalPrice?: number | null
}

export interface RawCard {
  transferList?: TransshipLeg[] | null
  ctnProduct?: Record<string, CtnPrice> | null
  fromServiceMode?: string | null
  toServiceMode?: string | null
  amsEns?: string | null
  [k: string]: unknown
}

export interface Sailing extends LaneRow {
  por_code: string | null
  pdl_code: string | null
  raw: RawCard | null
}

/** Bookable sailings on a lane, soonest first, with the database count of all of them. */
export async function getLaneSailings(
  pol: string,
  pod: string,
  client: SupabaseClient = dataClient
): Promise<{ sailings: Sailing[]; total: number }> {
  const today = new Date().toISOString().slice(0, 10)
  const { data, count, error } = await bookable(
    client
      .from('freight_rate_lane_cards')
      .select('*', { count: 'exact' })
      .eq('pol_code', pol)
      .eq('pod_code', pod),
    today,
    today
  )
    .order('etd', { ascending: true, nullsFirst: false })
    .limit(1000)
  if (error) throw error
  const sailings = (data ?? []) as Sailing[]
  return { sailings, total: count ?? sailings.length }
}

/** The cheapest sailing for the chosen box (earliest on a tie), and the newest refresh on the lane. */
export function laneHeadline(
  sailings: Sailing[],
  ctrKey: string
): { featured: Sailing | null; updatedAt: string | null } {
  const field = ctrKey === '20GP' ? 'price_20gp' : ctrKey === '40GP' ? 'price_40gp' : 'price_40hc'
  let featured: Sailing | null = null
  let updatedAt: string | null = null
  for (const s of sailings) {
    const price = s[field]
    if (price != null && price > 0 && (featured?.[field] == null || price < featured[field]))
      featured = s
    if (s.source_updated_at && (!updatedAt || s.source_updated_at > updatedAt))
      updatedAt = s.source_updated_at
  }
  return { featured: featured ?? sailings[0] ?? null, updatedAt }
}

export async function getLane(pol: string, pod: string): Promise<LaneRow | null> {
  const { data, error } = await dataClient
    .from('freight_rate_lane_latest')
    .select('*')
    .eq('pol_code', pol)
    .eq('pod_code', pod)
    .maybeSingle()
  if (error) throw error
  return (data as LaneRow) ?? null
}

// ── Lane price history (daily median, for the rate-trend chart) ──
export interface HistoryPoint {
  day: string
  median_40hc: number | null
  median_20gp: number | null
  sample: number
}

export async function getLaneHistory(pol: string, pod: string): Promise<HistoryPoint[]> {
  const { data, error } = await dataClient.rpc('public_lane_history', { p_pol: pol, p_pod: pod })
  if (error) throw error
  return (data ?? []) as HistoryPoint[]
}

// ── Trade-level rate trend (matched-basket ▲/▼ per Index card) ──
export interface IndexTrend {
  line_category_id: number
  days: number
  lanes: number
  latest_40hc: number
  earliest_40hc: number
  delta_40hc: number
  pct_change: number
}

/** Map of line_category_id → trend, for the Index cards. Only buckets with ≥5 matched lanes. */
export async function getIndexTrend(): Promise<Map<number, IndexTrend>> {
  const data = await withRetry(async () => {
    const { data, error } = await dataClient.from('freight_rate_index_trend').select('*')
    if (error) throw error
    return data
  })
  const m = new Map<number, IndexTrend>()
  for (const r of (data ?? []) as IndexTrend[]) {
    if (r.lanes >= 5 && r.days >= 2) m.set(r.line_category_id, r)
  }
  return m
}

// ── Rate alerts (Pro feature — watch a lane, get notified on a move) ──
export interface RateAlert {
  id: string
  pol_code: string
  pod_code: string
  pol_name: string | null
  pod_name: string | null
  direction: 'any' | 'drop' | 'spike'
  threshold_pct: number
  active: boolean
  created_at: string
}

export async function listAlerts(): Promise<RateAlert[]> {
  const { data, error } = await (await getActiveClient())
    .from('frm_rate_alerts')
    .select('*')
    .order('created_at', { ascending: false })
  if (error) throw error
  return (data ?? []) as RateAlert[]
}

/** Insert a lane watch. Returns 'limit' if the free 1-alert cap is hit, 'exists' if duplicate. */
export async function createAlert(a: {
  user_id: string
  pol_code: string
  pod_code: string
  pol_name?: string | null
  pod_name?: string | null
  threshold_pct?: number
}): Promise<{ error?: 'limit' | 'exists' | 'auth' | string }> {
  const { error } = await (await getActiveClient()).from('frm_rate_alerts').insert({
    user_id: a.user_id,
    pol_code: a.pol_code,
    pod_code: a.pod_code,
    pol_name: a.pol_name ?? null,
    pod_name: a.pod_name ?? null,
    direction: 'any',
    threshold_pct: a.threshold_pct ?? 5,
  })
  if (error) {
    if (error.message?.includes('FREE_ALERT_LIMIT')) return { error: 'limit' }
    if (error.code === '23505') return { error: 'exists' }
    return { error: error.message }
  }
  return {}
}

export async function deleteAlert(id: string): Promise<void> {
  const { error } = await (await getActiveClient()).from('frm_rate_alerts').delete().eq('id', id)
  if (error) throw error
}

// ── Lane reliability (schedulesmcp observed on-time %, joined by lane + carrier) ──
export interface LaneReliability {
  pol_code: string
  pod_code: string
  carrier: string
  on_time_pct: number
  observations: number
  avg_delay_hours: number | null
  schedule_integrity_pct: number | null
  window_days: number | null
}

export async function getLaneReliability(pol: string, pod: string): Promise<LaneReliability[]> {
  const { data, error } = await dataClient.rpc('public_lane_reliability', {
    p_pol: pol,
    p_pod: pod,
  })
  if (error) throw error
  return (data ?? []) as LaneReliability[]
}

export interface RfqPayload {
  name?: string
  email?: string
  company?: string
  pol_code?: string
  pol_name?: string
  pod_code?: string
  pod_name?: string
  container_type?: string
  ready_date?: string
  volume?: string
  incoterm?: string
  commodity?: string
  notes?: string
  /** UI language the lead was captured in (en, de, …). Powers in-language follow-up. */
  locale?: string
  // The exact sailing the shopper clicked (Book on a schedule row, or a carrier price chip in the
  // "same ship" comparison), so the pre-book is precise about which vessel/date/carrier/price they want.
  carrier?: string
  vessel?: string
  voyage?: string
  etd?: string
  sailing_price?: number
}

// The family intake API. Already used below for the sales notification, and now for the row itself.
const RFQ_INTAKE = 'https://api.trackingmcp.com/v1/rfq'

export async function submitRfq(payload: RfqPayload): Promise<void> {
  // The row used to be inserted straight into freight_rfq from here, through PostgREST under an anon
  // INSERT policy whose check was `true` — anyone, with no account, could write any row, and on
  // 2026-09-16 a probe did exactly that three times with an empty body. It now goes through
  // POST /v1/rfq, which verifies a Turnstile token and refuses a submission carrying no lead in it.
  //
  // `source` and `user_agent` are no longer sent: the server sets both. A client-supplied user agent
  // is just another field to lie in, and `source` is the column's own default.
  //
  // `locale` is a first-class column (migration 013) capturing the capture-time UI language for
  // in-language follow-up. Default to 'en' when unset.
  const token = await solveTurnstile()
  const res = await fetch(RFQ_INTAKE, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      // Absent when the challenge could not run at all (blocked script, outage, no key configured).
      // The server decides what that means — this form never refuses a shopper on its own.
      ...(token ? { 'x-turnstile-token': token } : {}),
    },
    body: JSON.stringify({ ...payload, locale: payload.locale ?? 'en' }),
  })
  // Throwing keeps the contract every caller already relies on (CnSponsorForm awaits and shows an
  // error). The message is the server's when it gave one, so a refused challenge or an empty
  // submission tells the shopper what to do instead of failing silently.
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as {
      error?: { message?: string }
    } | null
    throw new Error(body?.error?.message ?? 'We could not send that. Please try again.')
  }

  // Notify sales the way the rest of the family does: fire-and-forget POST to the shared
  // /v1/contact-sales intake (central sales_inquiries + email to the team), so an RFQ is not left sitting
  // silently in freight_rfq for someone to poll. Never blocks or fails the RFQ (the row above is the
  // source of truth); the intake needs a valid email, so skip when the shopper left it blank.
  if (payload.email) {
    const lane = `${payload.pol_name ?? payload.pol_code ?? '?'} to ${payload.pod_name ?? payload.pod_code ?? '?'}`
    const message = [
      'Freight rate RFQ (freightratesmcp).',
      `Lane: ${lane}`,
      `Container: ${payload.container_type ?? '-'} | Ready: ${payload.ready_date ?? '-'} | Volume: ${payload.volume ?? '-'} | Incoterm: ${payload.incoterm ?? '-'}`,
      payload.commodity ? `Commodity: ${payload.commodity}` : null,
      payload.carrier
        ? `Sailing: ${payload.carrier} ${payload.vessel ?? ''} ${payload.voyage ?? ''} ETD ${payload.etd ?? ''}`.trim()
        : null,
      payload.notes ? `Notes: ${payload.notes}` : null,
      'Full detail captured in freight_rfq.',
    ]
      .filter(Boolean)
      .join('\n')
    void fetch('https://api.trackingmcp.com/v1/contact-sales', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: payload.name,
        email: payload.email,
        company: payload.company,
        message,
        topic: 'other',
        source: 'freightrates',
      }),
    }).catch(() => {})
  }
}
